import React, { useMemo, useState, useEffect } from 'react';
import Input, { InputProps, InputClassKey } from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { ClassNameMap } from '@material-ui/styles/withStyles';
import Skeleton from '@material-ui/lab/Skeleton';
import clsx from 'clsx';
import { useField } from 'formik';

import { useGDirection, useTextFieldError } from '../hooks';
import { confirmRegex } from '../constants';
import FormHelperText from './FormHelperText';

import { usePropagateRef } from "../functions";


export interface FormFastInputProps extends InputProps {
    id: string;
    description: string;
    loading?: boolean;
    helperText?: string;
    /**
     * IF true, it will use the traditional method for disabling performance
     */
    onValueChange?: (val: string | number) => void;

    labelAdornment?: React.ReactNode;
    classes?: Partial<ClassNameMap<InputClassKey | 'container' | 'labelContainer' | 'label' | 'maxLength' | 'helperText' | 'errorContainer'>>;
}


export const FormFastInput: React.FC<FormFastInputProps> = React.memo(
    ({ id, type, dir, description, loading, onValueChange, helperText, autoComplete: ac, labelAdornment, classes: { container = "", labelContainer = "", label = "", maxLength: maxLengthClass = "", helperText: helperTextClassName = "", errorContainer = "", ...inputClasses } = {}, ...rest }) => {
        const [field, meta, helpers] = useField(id);
        const fieldError = useTextFieldError(id);
        const direction = useGDirection();

        const classes = useFormInputStyles();

        const maxLength = useMemo<number | undefined>(() => rest.inputProps?.maxLength, [rest.inputProps?.maxLength]);

        const autoComplete = useMemo(() => {
            return ac ?? confirmRegex.test(id) ? 'off' : undefined;
        }, [id, ac]);


        /**
         * For performance reasons (possible due to CSS in JS issues), heavy views
         * affect re-renders (Formik changes state in every re-render), bringing keyboard
         * input to its knees. To control this, we create a setState that handles the field's inner
         * (otherwise you wouldn't be able to type) and then propagate the change to Formik onBlur and
         * onFocus.
         */
        const [fieldValue, setFieldValue] = useState<string | number>(field.value);
        usePropagateRef({
            setFieldValue,
            name: id,
            value: field.value,
        });

        /**
         * Using this useEffect guarantees us that pre-filled forms
         * such as passwords work.
         */
        useEffect(() => {
            if (meta.touched) {
                return;
            }

            if (field.value !== fieldValue) {
                setFieldValue(field.value);
            }
            // eslint-disable-next-line
        }, [field.value]);

        const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(evt.target.value);
        };
        const onBlur = (evt: React.FocusEvent<HTMLInputElement>) => {
            field.onChange({
                target: {
                    name: id,
                    value: type === "number" ? parseInt(fieldValue as string, 10) : fieldValue,
                },
            });

            setTimeout(() => field.onBlur(evt), 0);//wait for onChange to finish and then blur.
            onValueChange && onValueChange(fieldValue)
        };



        return (
            <div className={container}>
                <div className={clsx(classes.containLables, labelContainer)}>
                    <InputLabel htmlFor={id} className={clsx(classes.label, label)}>{description}</InputLabel>
                    {maxLength ? <div className={maxLengthClass}>{(fieldValue ? (fieldValue as string).length : 0) + '/' + maxLength}</div> : null}
                    {labelAdornment && labelAdornment}
                </div>
                {loading ? (
                    <Skeleton
                        width="100%"
                        variant="rect"
                        className={clsx(classes.input, classes.skeleton, fieldError && classes.border)}
                    />
                ) : (
                    <Input
                        id={id}
                        type={type}
                        disableUnderline
                        dir={dir || direction}
                        className={clsx(classes.input, fieldError && classes.border)}
                        classes={inputClasses}
                        autoComplete={autoComplete}
                        {...field}
                        value={loading ? "..." : fieldValue}
                        onChange={onChange}
                        onBlur={onBlur}
                        {...rest}

                    />
                )}
                {helperText && <FormHelperText className={helperTextClassName} text={helperText} />}
                <FormHelperText className={errorContainer} text={fieldError} error />
            </div>

        );
    }
);

export const useFormInputStyles = makeStyles(theme => ({
    label: {
        color: theme.palette.primary.main,
        textAlign: 'left',
        fontWeight: 500,
        fontFamily: 'Assistant-SemiBold',
        [theme.breakpoints.up(500)]: {
            fontSize: 13,
        },
        [theme.breakpoints.down(500)]: {
            fontSize: 15,
        },
        [theme.breakpoints.up(1600)]: {
            fontSize: 17,
        }
    },

    containLables: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 13,
        [theme.breakpoints.up(1600)]: {
            fontSize: 17,
        },
        color: '#a5a5a5',
        marginTop: '5px',
    },

    skeleton: {
        borderRadius: 5,
        [theme.breakpoints.up(500)]: {
            height: 28,
        },
        [theme.breakpoints.down(500)]: {
            height: 37
        },
        [theme.breakpoints.up(1600)]: {
            height: 45
        },
    },

    input: {
        color: theme.palette.primary.main,
        display: 'flex',
        marginTop: 5,
        backgroundColor: '#F5F5F5',
        fontFamily: 'Assistant-Regular',
        borderRadius: 5,
        padding: 'unset',
        border: '1px solid white',
        transition: 'background-color 0.5s ease',

        '&:hover, &:focus-within': {
            backgroundColor: '#ECECEC',
        },

        '& input , & textarea': {
            borderRadius: 5,
            [theme.breakpoints.up(500)]: {
                padding: '4px 7px',
                fontSize: 17,
            },
            [theme.breakpoints.down(500)]: {
                padding: '6px 7px',
                fontSize: 22,
            },
            [theme.breakpoints.up(1600)]: {
                padding: '9px 10px',
                fontSize: 23,
            },
        },

        '& textarea': {
            lineHeight: 1.3
        }
    },

    border: {
        borderColor: theme.palette.error.main,
        borderStyle: 'solid',
        [theme.breakpoints.up(500)]: {
            borderWidth: 1
        },
        [theme.breakpoints.down(500)]: {
            borderWidth: 1.5
        }
    }
}))

export default React.memo(FormFastInput);
